import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { SubItem, SubItemStatus, Source, Release, Status, Environment } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Edit, Trash, Info, CheckCircle, Clock, Circle, Pause, AlertTriangle } from 'lucide-react';
import { NativeDialog, NativeDialogContent, NativeDialogHeader, NativeDialogTitle, NativeDialogDescription, NativeDialogFooter } from '@/components/ui/native-dialog';
import { Card } from '@/components/ui/card';
import InlineSubItemForm from './InlineSubItemForm';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { 
  DetailsItem, 
  DetailsSection, 
  DetailsBadgeList, 
  DetailsLink, 
  DetailsText, 
  DetailsNotes, 
  DetailsIcons 
} from '@/components/ui/details';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { useToast } from '@/components/ui/use-toast';

// Custom TableRow without hover effect
const NoHoverTableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b transition-colors data-[state=selected]:bg-muted", // Removed hover:bg-muted/50
      className
    )}
    {...props}
  />
));
NoHoverTableRow.displayName = "NoHoverTableRow";

interface SubItemListProps {
  subItems: SubItem[];
  updateSubItem: (subItem: SubItem) => void;
  deleteSubItem: (id: string) => void;
  canEdit?: boolean;
  releaseId: string; // Required prop for the current release ID
  clientId?: string; // Optional client ID for fetching available releases
  showActions?: boolean; // Whether to show edit/delete actions
  addSubItem?: (subItem: SubItem) => void; // Function to add a new sub item
}

// Define the ref handle type
export interface SubItemListHandle {
  openAddItemForm: () => void;
}

// Status display names for section headers
const statusDisplayNames: Record<SubItemStatus, string> = {
  'ready': 'Completed Items',
  'in progress': 'In Progress Items',
  'not started': 'Not Started Items',
  'on hold': 'On Hold Items'
};

// Header background colors for status sections
const statusHeaderColors: Record<SubItemStatus, string> = {
  'ready': 'bg-[#10b981] text-white', // status-completed
  'in progress': 'bg-[#f59e0b] text-white', // status-in-progress
  'not started': 'bg-[#64748b] text-white', // status-not-started
  'on hold': 'bg-[#ef4444] text-white' // status-on-hold
};

// Border colors for status sections
const statusBorderColors: Record<SubItemStatus, string> = {
  'ready': 'border-[#10b981]', // status-completed
  'in progress': 'border-[#f59e0b]', // status-in-progress
  'not started': 'border-[#64748b]', // status-not-started
  'on hold': 'border-[#ef4444]' // status-on-hold
};

const sourceColors: Record<Source, string> = {
  'agency': 'bg-blue-100 text-blue-800',
  'client': 'bg-purple-100 text-purple-800'
};

const SubItemList = forwardRef<SubItemListHandle, SubItemListProps>(({ 
  subItems, 
  updateSubItem, 
  deleteSubItem, 
  canEdit = true,
  releaseId,
  clientId,
  showActions = false,
  addSubItem
}, ref) => {
  const { toast } = useToast();
  const [showSubItemForm, setShowSubItemForm] = useState(false);
  const [editingSubItem, setEditingSubItem] = useState<SubItem | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedSubItem, setSelectedSubItem] = useState<SubItem | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [subItemToDelete, setSubItemToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [availableReleases, setAvailableReleases] = useState<Release[]>([]);
  
  // Fetch available releases for the current client
  useEffect(() => {
    const fetchReleases = async () => {
      if (!clientId) return;
      
      try {
        const { data: releasesData, error } = await supabase
          .from('releases')
          .select('*')
          .eq('client_id', clientId)
          .order('created_at', { ascending: false });

        if (error) throw error;

        const releases: Release[] = releasesData.map(release => ({
          id: release.id,
          version: release.version,
          status: release.status as Status,
          environment: release.environment as Environment,
          brdLink: release.brd_link || '',
          approvedBy: release.approved_by || '',
          notes: release.notes || '',
          createdAt: new Date(release.created_at),
          plannedReleaseDate: release.planned_release_date ? new Date(release.planned_release_date) : undefined,
          subItems: [],
          clientId: release.client_id,
          ownerId: release.owner_id,
        }));

        setAvailableReleases(releases);
      } catch (error) {
        console.error('Error fetching releases:', error);
        toast({
          title: 'Error',
          description: 'Failed to load available releases',
          variant: 'destructive'
        });
      }
    };

    fetchReleases();
  }, [clientId, toast]);

  // Function to open the add item form
  const openAddItemForm = () => {
    console.log('openAddItemForm called');
    setEditingSubItem(null);
    setShowSubItemForm(true);
    console.log('showSubItemForm set to true');
  };

  // Expose methods via ref
  useImperativeHandle(ref, () => {
    console.log('Setting up ref with openAddItemForm');
    return {
      openAddItemForm
    };
  }, []); // Empty dependency array since openAddItemForm is stable

  const handleUpdateSubItem = (subItem: SubItem) => {
    console.log("Updating sub item:", subItem);
    
    // Always use updateSubItem for both regular updates and release changes
    updateSubItem(subItem);
    
    // Show appropriate toast message based on whether the item was moved
    if (subItem.releaseId !== releaseId) {
      toast({
        title: "Sub item moved",
        description: `"${subItem.name}" was moved to release ${availableReleases.find(r => r.id === subItem.releaseId)?.version || 'another release'}`,
        variant: "default"
      });
    } else {
      toast({
        title: "Sub item updated",
        description: `"${subItem.name}" was updated successfully`,
        variant: "default"
      });
    }
    
    setShowSubItemForm(false);
    setEditingSubItem(null);
  };
  
  const handleDeleteSubItem = (id: string) => {
    setSubItemToDelete(id);
    setShowDeleteConfirm(true);
  };
  
  const confirmDeleteSubItem = () => {
    if (!subItemToDelete) return;
    
    setIsDeleting(true);
    console.log("Deleting sub item:", subItemToDelete);
    deleteSubItem(subItemToDelete);
    
    toast({
      title: "Sub item deleted",
      description: "The sub item was removed successfully",
      variant: "default"
    });
    
    setIsDeleting(false);
    setShowDeleteConfirm(false);
    setSubItemToDelete(null);
  };

  // Group sub items by status
  const groupedSubItems = subItems.reduce((acc, item) => {
    const status = item.status as SubItemStatus;
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(item);
    return acc;
  }, {} as Record<SubItemStatus, SubItem[]>);

  const statusOrder: SubItemStatus[] = ['ready', 'in progress', 'not started', 'on hold'];

  return (
    <div className="overflow-x-auto">
      {subItems.length === 0 ? (
        <div className="text-center py-8 border rounded-md bg-gray-50">
          <p className="text-gray-500 mb-4">No sub items added yet.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {statusOrder.map(status => {
            const items = groupedSubItems[status] || [];
            if (items.length === 0) return null;
            
            return (
              <div key={status} className={`rounded-md overflow-hidden border-2 ${statusBorderColors[status]} shadow-md mb-6`}>
                <div className={`px-4 py-3 font-medium ${statusHeaderColors[status]}`}>
                  <h3 className="text-sm flex items-center">
                    {status === 'ready' && <CheckCircle size={16} className="mr-2" />}
                    {status === 'in progress' && <Clock size={16} className="mr-2" />}
                    {status === 'not started' && <Circle size={16} className="mr-2" />}
                    {status === 'on hold' && <Pause size={16} className="mr-2" />}
                    {statusDisplayNames[status]}
                    <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">{items.length}</span>
                  </h3>
                </div>
                
                <div className="bg-white">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Approved By</TableHead>
                        <TableHead>Source Link</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map(item => (
                        <NoHoverTableRow key={item.id}>
                          <TableCell className="font-medium">
                            <button 
                              onClick={() => {
                                setSelectedSubItem(item);
                                setShowDetailsDialog(true);
                              }}
                              className="text-left hover:text-blue-600 hover:underline focus:outline-none"
                            >
                              {item.name}
                            </button>
                          </TableCell>
                          <TableCell>
                            <Badge className={sourceColors[item.source as Source]}>
                              {item.source}
                            </Badge>
                          </TableCell>
                          <TableCell>{item.approvedBy || '—'}</TableCell>
                          <TableCell>
                            {item.sourceLink ? (
                              <a 
                                href={item.sourceLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                View Source
                              </a>
                            ) : '—'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => {
                                  setSelectedSubItem(item);
                                  setShowDetailsDialog(true);
                                }}
                              >
                                <Info size={16} />
                              </Button>
                              
                              {showActions && canEdit && (
                                <>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => {
                                      setEditingSubItem(item);
                                      setShowSubItemForm(true);
                                    }}
                                  >
                                    <Edit size={16} />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleDeleteSubItem(item.id)}
                                  >
                                    <Trash size={16} />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </NoHoverTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Form Dialog */}
      <NativeDialog open={showSubItemForm} onOpenChange={(open) => {
        if (!open) {
          setEditingSubItem(null);
        }
        setShowSubItemForm(open);
      }}>
        <NativeDialogContent>
          <InlineSubItemForm
            subItem={editingSubItem}
            onSubmit={(submittedSubItem) => {
              if (editingSubItem) {
                handleUpdateSubItem(submittedSubItem);
              } else if (addSubItem) {
                addSubItem(submittedSubItem);
                toast({
                  title: "Sub item added",
                  description: `"${submittedSubItem.name}" was added successfully`,
                  variant: "default"
                });
                setShowSubItemForm(false);
                setEditingSubItem(null);
              }
            }}
            onCancel={() => {
              setShowSubItemForm(false);
              setEditingSubItem(null);
            }}
            releaseId={releaseId}
            availableReleases={availableReleases}
            currentClientId={clientId}
          />
        </NativeDialogContent>
      </NativeDialog>

      {/* Details Dialog */}
      <NativeDialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog} size="3xl">
        <NativeDialogContent>
          <NativeDialogHeader>
            <NativeDialogTitle>
              {selectedSubItem?.name}
            </NativeDialogTitle>
            {selectedSubItem?.type && (
              <NativeDialogDescription>
                {selectedSubItem.type}
              </NativeDialogDescription>
            )}
          </NativeDialogHeader>
          
          {selectedSubItem && (
            <div className="py-4 space-y-6">
              {/* Status Card */}
              <Card className="p-4">
                <StatusIndicator currentStatus={selectedSubItem.status as SubItemStatus} className="w-full" />
              </Card>
              
              {/* Two-column layout for details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left column: Basic details */}
                <Card className="p-4">
                  <DetailsSection title="Details">
                    <div className="space-y-4">
                      <DetailsItem icon={<DetailsIcons.User size={16} />} label="Source">
                        <Badge className={sourceColors[selectedSubItem.source as Source]}>
                          {selectedSubItem.source}
                        </Badge>
                      </DetailsItem>
                      
                      <DetailsText 
                        text={selectedSubItem.approvedBy} 
                        label="Approved By" 
                        icon={<DetailsIcons.User size={16} />}
                      />
                      
                      <DetailsLink 
                        href={selectedSubItem.sourceLink} 
                        label="Source Link" 
                      />
                    </div>
                  </DetailsSection>
                </Card>
                
                {/* Right column: Affected items */}
                {(selectedSubItem.pagesAffected?.length > 0 || 
                  selectedSubItem.collectionsAffected?.length > 0 || 
                  selectedSubItem.componentsAffected?.length > 0) && (
                  <Card className="p-4">
                    <DetailsSection title="Affected Items">
                      <div className="space-y-4">
                        <DetailsBadgeList 
                          items={selectedSubItem.pagesAffected} 
                          icon={<DetailsIcons.Layout size={16} />} 
                          label="Pages"
                        />
                        
                        <DetailsBadgeList 
                          items={selectedSubItem.collectionsAffected} 
                          icon={<DetailsIcons.Database size={16} />} 
                          label="Collections"
                        />
                        
                        <DetailsBadgeList 
                          items={selectedSubItem.componentsAffected} 
                          icon={<DetailsIcons.Code size={16} />} 
                          label="Components"
                        />
                      </div>
                    </DetailsSection>
                  </Card>
                )}
              </div>
              
              {/* Notes section - full width */}
              {selectedSubItem.notes && (
                <Card className="p-4">
                  <DetailsNotes notes={selectedSubItem.notes} />
                </Card>
              )}
            </div>
          )}
          
          <NativeDialogFooter>
            <Button onClick={() => setShowDetailsDialog(false)}>Close</Button>
          </NativeDialogFooter>
        </NativeDialogContent>
      </NativeDialog>

      {/* Delete Confirmation Dialog */}
      <NativeDialog open={showDeleteConfirm} size="xl" onOpenChange={setShowDeleteConfirm}>
        <NativeDialogContent>
          <NativeDialogHeader>
            <NativeDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Confirm Deletion
            </NativeDialogTitle>
            <NativeDialogDescription>
              Are you sure you want to delete this item? This action cannot be undone.
            </NativeDialogDescription>
          </NativeDialogHeader>
          <NativeDialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteConfirm(false);
                setSubItemToDelete(null);
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteSubItem}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Yes, Delete Item'}
            </Button>
          </NativeDialogFooter>
        </NativeDialogContent>
      </NativeDialog>
    </div>
  );
});

export default SubItemList;
